import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Grid, Card, CardContent, Typography, Button, TextField,
  MenuItem, Chip, IconButton, Tooltip, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Avatar,
  InputAdornment, Divider, List, ListItem, ListItemAvatar,
  ListItemText, LinearProgress, Tabs, Tab,
} from "@mui/material";
import {
  Add, Search, Edit, Delete, People,
  Code, Cloud, Storage, AutoGraph, Brush, Star,
  Psychology, Devices, Category, Close as CloseIcon,
  WorkOutline, TrendingUp, Layers,
} from "@mui/icons-material";
import styled, { keyframes } from "styled-components";
import { useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import recruiterTheme, { FONT_BODY, FONT_DISPLAY, COLORS, TYPE, RADIUS, ELLIPSIS, PAGE_PADDING } from "../../styles/recruiterTheme";

const BASE = process.env.REACT_APP_API_BASE_URL;
const getHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token") || ""}`,
});
const handle = async (res) => {
  // Tolerate empty / non-JSON bodies (e.g. 204 on delete, HTML error pages)
  const text = await res.text();
  let data = {};
  try { data = text ? JSON.parse(text) : {}; } catch { data = {}; }
  if (!res.ok) {
    const body = data && typeof data === "object" ? data : {};
    const err = { ...body, status: res.status };
    if (typeof err.message !== "string" || !err.message) err.message = errMsg(body, `Request failed (${res.status})`);
    throw err;
  }
  return data;
};

const getAllSkills     = (p = {}) => {
  const qs = new URLSearchParams(p).toString();
  return fetch(`${BASE}/skills/${qs ? "?" + qs : ""}`, { headers: getHeaders() }).then(handle);
};
const createSkill     = (pl) => fetch(`${BASE}/skills/`,     { method: "POST",   headers: getHeaders(), body: JSON.stringify(pl) }).then(handle);
const updateSkill     = (id, pl) => fetch(`${BASE}/skills/${id}`, { method: "PUT",    headers: getHeaders(), body: JSON.stringify(pl) }).then(handle);
const deleteSkill     = (id) => fetch(`${BASE}/skills/${id}`, { method: "DELETE", headers: getHeaders() }).then(handle);
const getResumesBySkill = (name) =>
  fetch(`${BASE}/resumes/by-skill/${encodeURIComponent(name)}`, { headers: getHeaders() }).then(handle);
const getBenchBySkill = (name) =>
  fetch(`${BASE}/bench/by-skill/${encodeURIComponent(name)}`, { headers: getHeaders() }).then(handle);
const talentSearchResumes = (q, signal) =>
  fetch(`${BASE}/resumes/talent-search?q=${encodeURIComponent(q)}`, { headers: getHeaders(), signal }).then(handle);
const talentSearchBench = (q, signal) =>
  fetch(`${BASE}/bench/talent-search?q=${encodeURIComponent(q)}`, { headers: getHeaders(), signal }).then(handle);
const getSkillInsights = (id) =>
  fetch(`${BASE}/skills/${id}/insights`, { headers: getHeaders() }).then(handle);

// ─── Shared design tokens (matches BenchPeople's purple design system) ────────
const BRAND        = COLORS.primary;      // #6750A4
const BRAND_DARK   = COLORS.primaryDark;  // #4F378B
const BRAND_DEEP   = COLORS.primaryDeep;  // #2D1B5E
const BRAND_SOFT   = COLORS.muted;        // #8B7DB5
const BRAND_TINT2  = COLORS.border;       // #EDE7F6
const R_SM = `${RADIUS.sm}px`;
const R_MD = `${RADIUS.md}px`;
const R_LG = `${RADIUS.lg}px`;
const R_PILL = `${RADIUS.pill}px`;
const CARD_SHADOW = "0 3px 18px rgba(79,55,139,0.08)";
// Uniform chip sizes: skill tags (12px, 8px radius) and status pills (11px, full round)
const tagSx  = { height: 24, fontSize: TYPE.caption, borderRadius: R_SM, fontFamily: FONT_BODY, "& .MuiChip-label": { px: 1, fontSize: TYPE.caption } };
const pillSx = { height: 24, fontSize: TYPE.overline, borderRadius: R_PILL, fontFamily: FONT_BODY, fontWeight: 700, "& .MuiChip-label": { px: 1, fontSize: TYPE.overline } };
const countSx = { height: 20, fontSize: TYPE.overline, borderRadius: R_PILL, fontFamily: FONT_BODY, fontWeight: 700, "& .MuiChip-label": { px: 0.75, fontSize: TYPE.overline } };
// Same pill look for the plain-Box status badges in talent search
const boxPillSx = { display: "inline-flex", alignItems: "center", height: 24, px: 1, borderRadius: R_PILL, fontSize: TYPE.overline, fontWeight: 700, fontFamily: FONT_BODY, whiteSpace: "nowrap" };
const emptySx = { display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", py: 5, px: 2, gap: 1.5 };

const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(14px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const GradientHeading = styled(Typography)`
  && {
    font-family: ${FONT_DISPLAY} !important;
    font-weight: 700 !important;
    letter-spacing: -0.02em;
    background: linear-gradient(135deg, #1A0A3D 0%, #6750A4 50%, #9C7FD4 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    line-height: 1.25 !important;
  }
`;

const StatCardGrad = styled.div`
  background: ${p => p.gradient || "linear-gradient(135deg,#6750A4,#4F378B)"};
  border-radius: ${RADIUS.md}px;
  padding: 16px 24px;
  color: #fff;
  position: relative;
  overflow: hidden;
  box-shadow: 0 5px 18px ${p => p.shadow || "rgba(103,80,164,0.26)"};
  transition: transform 0.2s, box-shadow 0.2s;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  min-width: 0;
  &:hover { transform: translateY(-2px); box-shadow: 0 10px 24px ${p => p.shadow || "rgba(103,80,164,0.36)"}; }
  &::after {
    content: '';
    position: absolute;
    top: -22px; right: -22px;
    width: 70px; height: 70px;
    border-radius: 50%;
    background: rgba(255,255,255,0.1);
  }
  @media (max-width: 599px) {
    padding: 12px 16px;
    gap: 12px;
  }
`;

const StatIconBubble = styled.div`
  width: 44px; height: 44px; border-radius: ${RADIUS.md}px;
  background: rgba(255,255,255,0.16);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  svg { font-size: 22px; color: #fff; }
  @media (max-width: 599px) {
    width: 36px; height: 36px; border-radius: ${RADIUS.sm}px;
    svg { font-size: 18px; }
  }
`;

const StatNumGrad = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.stat}px;
  font-weight: 800;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  @media (max-width: 599px) {
    font-size: ${TYPE.section}px;
  }
`;

const StatLblGrad = styled.div`
  font-family: ${FONT_BODY};
  font-size: ${TYPE.overline}px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  opacity: 0.88;
  margin-bottom: 8px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SectionLabel = styled(Typography)`
  && {
    font-family: ${FONT_BODY} !important;
    font-weight: 700 !important;
    font-size: ${TYPE.cardTitle}px !important;
    line-height: 1.25 !important;
    color: #2D1B5E;
    display: flex;
    align-items: center;
    gap: 8px;
    margin: 0 0 16px !important;
    &::before {
      content: '';
      width: 4px; height: 18px;
      border-radius: 2px;
      background: linear-gradient(180deg, #6750A4, #4F378B);
    }
  }
`;

// Inside RecruiterDashboard the container already provides PAGE_PADDING; standalone (/skills) adds its own.
const PageRoot = styled.div`
  font-family: ${FONT_BODY};
  background: #F3EEF9;
  min-height: ${p => (p.$standalone ? "100vh" : "0")};
  padding: ${p => (p.$standalone ? `${PAGE_PADDING}px` : "0")};
  @media (max-width: 599px) {
    padding: ${p => (p.$standalone ? "16px" : "0")};
  }
  width: 100%;
  box-sizing: border-box;
  animation: ${fadeSlideIn} 0.35s ease both;
`;

// ── Constants ─────────────────────────────────────────────────────────────────
const CATEGORIES = [
  "Programming Languages","Frameworks & Libraries","Databases",
  "Cloud & DevOps","Data Science & ML","Design & UX",
  "Management & Soft Skills","Domain Expertise","Tools & Platforms","Other",
];
const DEMAND_LEVELS = ["Critical","High","Medium","Low"];

const DEMAND_META = {
  Critical: { color: "#b91c1c", bg: "#fef2f2", border: "#fecaca" },
  High:     { color: "#c2410c", bg: "#fff7ed", border: "#fed7aa" },
  Medium:   { color: "#0369a1", bg: "#f0f9ff", border: "#bae6fd" },
  Low:      { color: "#4b5563", bg: "#f9fafb", border: "#e5e7eb" },
};

const BENCH_STATUS_COLOR = {
  Available:      { bg: "#e8f5e9", color: "#2e7d32", border: "#a5d6a7" },
  "In Interview": { bg: "#fff3e0", color: "#e65100", border: "#ffcc80" },
  Deployed:       { bg: "#e3f2fd", color: "#0277bd", border: "#90caf9" },
  "On Hold":      { bg: "#f3e5f5", color: "#7b1fa2", border: "#ce93d8" },
  Resigned:       { bg: "#f5f5f5", color: "#757575", border: "#e0e0e0" },
};
const EMPLOYMENT_TYPE_COLOR = {
  Permanent: "#1a237e", Contract: "#4a148c", C2H: "#1b5e20", Freelance: "#e65100",
};

const CATEGORY_ICON = {
  "Programming Languages": <Code />,
  "Frameworks & Libraries": <Devices />,
  "Databases": <Storage />,
  "Cloud & DevOps": <Cloud />,
  "Data Science & ML": <AutoGraph />,
  "Design & UX": <Brush />,
  "Management & Soft Skills": <Psychology />,
  "Domain Expertise": <Star />,
  "Tools & Platforms": <Devices />,
  "Other": <Category />,
};

const CATEGORY_PALETTE = {
  "Programming Languages":    { bg: "#eef2ff", color: "#4338ca", light: "#c7d2fe" },
  "Frameworks & Libraries":   { bg: "#eff6ff", color: "#1d4ed8", light: "#bfdbfe" },
  "Databases":                { bg: "#f0fdf4", color: "#15803d", light: "#bbf7d0" },
  "Cloud & DevOps":           { bg: "#faf5ff", color: "#7e22ce", light: "#e9d5ff" },
  "Data Science & ML":        { bg: "#f0fdfa", color: "#0f766e", light: "#99f6e4" },
  "Design & UX":              { bg: "#fdf2f8", color: "#be185d", light: "#fbcfe8" },
  "Management & Soft Skills": { bg: "#fff7ed", color: "#c2410c", light: "#fed7aa" },
  "Domain Expertise":         { bg: "#fdf4ff", color: "#9333ea", light: "#f3e8ff" },
  "Tools & Platforms":        { bg: "#f0f9ff", color: "#0369a1", light: "#bae6fd" },
  "Other":                    { bg: "#f8fafc", color: "#475569", light: "#cbd5e1" },
};

const EMPTY_FORM = {
  skill_name: "", category: "", proficiency_levels: "",
  description: "", demand_level: "Medium", related_skills: "",
};

const fmtSalary = (v) => {
  if (!v) return "—";
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  return `₹${Number(v).toLocaleString("en-IN")}`;
};
const getSkillNames = (skillsField) => {
  if (!skillsField) return [];
  if (Array.isArray(skillsField)) {
    return skillsField
      .map(s => (typeof s === "object" && s !== null ? s.name : s))
      .filter(Boolean);
  }
  return String(skillsField).split(",").map(s => s.trim()).filter(Boolean);
};
const nameInitials = (name = "") =>
  name.split(" ").filter(Boolean).map(w => w[0]).join("").toUpperCase().slice(0, 2) || "?";
// Loose skill/query match used to highlight matched skills in talent search results
const isMatch = (skillName, query) => {
  const s = skillName.toLowerCase().trim();
  const q = query.toLowerCase().trim();
  return s.includes(q) || q.includes(s) || q.split(/[\s,]+/).some(w => w.length >= 2 && s.includes(w));
};
const errMsg = (err, fallback) =>
  [err?.message, err?.error, err?.detail].find(m => typeof m === "string" && m) || fallback;

// ── Stat Card (gradient tile, matches BenchPeople's StatCard language) ───────
const StatCard = ({ label, value, icon, accent }) => (
  <StatCardGrad gradient={`linear-gradient(135deg, ${accent}, ${BRAND_DARK})`} shadow={`${accent}44`}>
    <Box minWidth={0} flex={1}>
      <StatLblGrad title={label}>{label}</StatLblGrad>
      <StatNumGrad>{value}</StatNumGrad>
    </Box>
    <StatIconBubble>{icon}</StatIconBubble>
  </StatCardGrad>
);

// ── Skill Resumes Dialog ──────────────────────────────────────────────────────
function SkillResumesDialog({ open, onClose, skill }) {
  const [tab, setTab]                   = useState(0);
  const [resumes, setResumes]           = useState([]);
  const [bench, setBench]               = useState([]);
  const [insights, setInsights]         = useState(null);
  const [loading, setLoading]           = useState(false);
  const [insightsLoad, setInsightsLoad] = useState(false);
  const [error, setError]               = useState("");
  const [warning, setWarning]           = useState("");
  const [insightsError, setInsightsError] = useState(false);
  const [insightsRetry, setInsightsRetry] = useState(0);
  const [minMatch, setMinMatch]         = useState(1);
  const [availOnly, setAvailOnly]       = useState(false);

  useEffect(() => {
    if (!open || !skill) return;
    let ignore = false;
    setLoading(true); setError(""); setWarning(""); setResumes([]); setBench([]);
    setTab(0); setAvailOnly(false);

    const relatedList = (skill.related_skills || "").split(",").map(s => s.trim()).filter(Boolean);
    const allNames    = [skill.skill_name, ...relatedList];
    // Default to 1 so the dialog count matches the table's Candidates count
    setMinMatch(1);

    let resumeFails = 0;
    let benchFails  = 0;
    Promise.all([
      Promise.all(allNames.map(name => getResumesBySkill(name).catch(() => { resumeFails++; return { data: [] }; }))),
      Promise.all(allNames.map(name => getBenchBySkill(name).catch(() => { benchFails++; return { data: [] }; }))),
    ]).then(([resumeResults, benchResults]) => {
      if (ignore) return;
      const n = allNames.length;
      if (resumeFails === n && benchFails === n) {
        setError("Failed to load candidates and bench people. Please try again.");
        return;
      }
      if (resumeFails === n)     setWarning("Could not load candidates — showing bench people only.");
      else if (benchFails === n) setWarning("Could not load bench people — showing candidates only.");
      else if (resumeFails || benchFails) setWarning("Some skill lookups failed — results may be incomplete.");

      const rMap = new Map();
      resumeResults.forEach((res, idx) => {
        (res.data || []).forEach(r => {
          if (!rMap.has(r._id)) rMap.set(r._id, { ...r, matchedSkills: [], matchCount: 0 });
          const e = rMap.get(r._id);
          if (!e.matchedSkills.includes(allNames[idx])) {
            e.matchedSkills.push(allNames[idx]); e.matchCount++;
          }
        });
      });
      setResumes(Array.from(rMap.values()).sort((a, b) => b.matchCount - a.matchCount));

      const bMap = new Map();
      benchResults.forEach((res, idx) => {
        (res.data || []).forEach(b => {
          if (!bMap.has(b._id)) bMap.set(b._id, { ...b, matchedSkills: [], matchCount: 0 });
          const e = bMap.get(b._id);
          if (!e.matchedSkills.includes(allNames[idx])) {
            e.matchedSkills.push(allNames[idx]); e.matchCount++;
          }
        });
      });
      setBench(Array.from(bMap.values()).sort((a, b) => {
        if (a.status === "Available" && b.status !== "Available") return -1;
        if (b.status === "Available" && a.status !== "Available") return 1;
        return b.matchCount - a.matchCount;
      }));
    }).catch(err => { if (!ignore) setError(errMsg(err, "Failed to load")); })
      .finally(() => { if (!ignore) setLoading(false); });
    return () => { ignore = true; };
  }, [open, skill]);

  useEffect(() => { setInsights(null); setInsightsError(false); }, [skill]);

  useEffect(() => {
    if (tab !== 2 || !skill?._id || insights) return;
    let ignore = false;
    setInsightsLoad(true); setInsightsError(false);
    getSkillInsights(skill._id)
      .then(res => {
        if (ignore) return;
        setInsights(res.data || null);
        if (!res.data) setInsightsError(true);
      })
      .catch(() => { if (!ignore) { setInsights(null); setInsightsError(true); } })
      .finally(() => { if (!ignore) setInsightsLoad(false); });
    return () => { ignore = true; setInsightsLoad(false); };
  }, [tab, skill, insights, insightsRetry]);

  if (!skill) return null;

  const relatedList   = (skill.related_skills || "").split(",").map(s => s.trim()).filter(Boolean);
  const total         = 1 + relatedList.length;
  const palette       = CATEGORY_PALETTE[skill.category] || CATEGORY_PALETTE["Other"];
  const filteredResumes = resumes.filter(r => r.matchCount >= minMatch);
  const filteredBench   = bench.filter(b =>
    b.matchCount >= minMatch && (!availOnly || b.status === "Available")
  );
  const availableNow  = bench.filter(b => b.status === "Available").length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: R_LG, overflow: "hidden", maxHeight: "92vh", m: { xs: 1, sm: 3 }, width: { xs: "calc(100% - 16px)", sm: "auto" } } }}>
      <Box sx={{ bgcolor: palette.bg, px: { xs: 2, sm: 3 }, pt: 3, pb: 0, borderBottom: `1px solid ${BRAND_TINT2}` }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} mb={2}>
          <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
            <Box sx={{
              width: 44, height: 44, borderRadius: R_MD, bgcolor: palette.color, flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {React.cloneElement(CATEGORY_ICON[skill.category] || <Category />,
                { sx: { fontSize: 22, color: "#fff" } })}
            </Box>
            <Box minWidth={0}>
              <Typography fontWeight={700} fontSize={TYPE.section} lineHeight={1.25} color={BRAND_DEEP} fontFamily={FONT_DISPLAY} title={skill.skill_name} sx={ELLIPSIS}>{skill.skill_name}</Typography>
              <Typography fontSize={TYPE.caption} color={BRAND_SOFT} fontFamily={FONT_BODY} mt={0.5}>{skill.category}</Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ width: 36, height: 36, flexShrink: 0 }}><CloseIcon fontSize="small" /></IconButton>
        </Box>

        {!loading && (
          <Box display="flex" gap={1} mb={2} flexWrap="wrap" alignItems="center">
            <Chip label={`${filteredResumes.length} Candidates`} size="small"
              sx={{ ...pillSx, bgcolor: palette.color, color: "#fff" }} />
            <Chip label={`${availableNow} Available Now`} size="small"
              sx={{ ...pillSx, bgcolor: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7" }} />
            <Chip label={`${filteredBench.length} Bench`} size="small"
              sx={{ ...pillSx, bgcolor: BRAND_TINT2, color: "#1a237e" }} />
            {relatedList.map(rel => (
              <Chip key={rel} label={rel} size="small"
                sx={{ ...tagSx, bgcolor: "#fff", color: palette.color,
                      border: `1px solid ${palette.light}` }} />
            ))}
          </Box>
        )}

        {!loading && (resumes.length > 0 || bench.length > 0) && total > 1 && (
          <Box display="flex" alignItems="center" gap={1.5} pb={1}>
            <Typography fontSize={TYPE.caption} color={BRAND_SOFT} fontWeight={600} fontFamily={FONT_BODY}>Min match:</Typography>
            <Box display="flex" gap={0.5}>
              {Array.from({ length: total }, (_, i) => i + 1).map(n => (
                <Box key={n} onClick={() => setMinMatch(n)} sx={{
                  width: 28, height: 28, borderRadius: R_SM,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  cursor: "pointer", fontSize: TYPE.caption, fontWeight: 700, fontFamily: FONT_BODY,
                  bgcolor: minMatch === n ? palette.color : "#fff",
                  color:   minMatch === n ? "#fff" : palette.color,
                  border: `1.5px solid ${minMatch === n ? palette.color : palette.light}`,
                  transition: "all 0.15s",
                }}>{n}</Box>
              ))}
            </Box>
            <Typography fontSize={TYPE.caption} color={BRAND_SOFT} fontFamily={FONT_BODY}>of {total}</Typography>
          </Box>
        )}

        <Tabs value={tab} onChange={(_, v) => setTab(v)}
          sx={{ minHeight: 44, "& .MuiTab-root": { fontSize: TYPE.small, fontWeight: 600, textTransform: "none", minHeight: 44, fontFamily: FONT_BODY } }}>
          <Tab label={`Candidates (${filteredResumes.length})`} />
          <Tab label={
            <Box display="flex" alignItems="center" gap={1}>
              Bench ({filteredBench.length})
              {availableNow > 0 && (
                <Chip label={`${availableNow} free`} size="small"
                  sx={{ ...countSx, bgcolor: "#e8f5e9", color: "#2e7d32" }} />
              )}
            </Box>
          } />
          <Tab label="Talent Insights" />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 0, overflow: "auto" }}>
        {loading && (
          <Box>
            <LinearProgress sx={{ "& .MuiLinearProgress-bar": { bgcolor: palette.color } }} />
            <Box sx={emptySx}>
              <Typography fontSize={TYPE.body} color={BRAND_SOFT} fontFamily={FONT_BODY}>Searching across candidates and bench…</Typography>
            </Box>
          </Box>
        )}
        {!loading && error && tab !== 2 && <Box p={3}><Alert severity="error" sx={{ borderRadius: R_SM }}>{error}</Alert></Box>}
        {!loading && !error && warning && tab !== 2 && <Box px={3} pt={2}><Alert severity="warning" sx={{ borderRadius: R_SM }}>{warning}</Alert></Box>}

        {/* Tab 0: Candidates */}
        {!loading && !error && tab === 0 && (
          filteredResumes.length === 0 ? (
            <Box sx={emptySx}>
              <People sx={{ fontSize: 40, color: "#e0e0e0" }} />
              <Typography fontSize={TYPE.body} color={BRAND_SOFT} fontWeight={500} fontFamily={FONT_BODY}>No candidates found</Typography>
            </Box>
          ) : (
            <List disablePadding>
              {filteredResumes.map((r, i) => {
                const allSkillsArr = getSkillNames(r.skills);
                const pct = Math.round((r.matchCount / total) * 100);
                const isPerfect = r.matchCount === total && total > 1;
                const bsColor = BENCH_STATUS_COLOR[r.status] || { bg: "#f5f5f5", color: "#757575", border: "#e0e0e0" };
                return (
                  <React.Fragment key={r._id}>
                    {i > 0 && <Divider />}
                    <ListItem alignItems="flex-start" sx={{ px: { xs: 1.75, sm: 3 }, py: 2, "&:hover": { bgcolor: "#FAF7FF" } }}>
                      <ListItemAvatar sx={{ minWidth: 56 }}>
                        <Box sx={{ position: "relative", display: "inline-block" }}>
                          <Avatar sx={{ bgcolor: isPerfect ? "#15803d" : palette.color, fontWeight: 700, fontSize: 13, width: 40, height: 40, fontFamily: FONT_BODY }}>
                            {nameInitials(r.name)}
                          </Avatar>
                          <Box sx={{
                            position: "absolute", bottom: -4, right: -6,
                            bgcolor: isPerfect ? "#15803d" : palette.color,
                            color: "#fff", fontSize: TYPE.overline, fontWeight: 700,
                            px: 0.5, borderRadius: "4px", border: "1.5px solid #fff", lineHeight: 1.4,
                          }}>{r.matchCount}/{total}</Box>
                        </Box>
                      </ListItemAvatar>
                      <ListItemText disableTypography primary={
                        <Box>
                          <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                            <Typography fontWeight={700} fontSize={TYPE.body} color={BRAND_DEEP} fontFamily={FONT_BODY}>{r.name}</Typography>
                            {isPerfect && <Chip label="Perfect match" size="small" sx={{ ...pillSx, bgcolor: "#f0fdf4", color: "#15803d", border: "1px solid #bbf7d0" }} />}
                            <Chip label={r.status} size="small" sx={{ ...pillSx, bgcolor: bsColor.bg, color: bsColor.color, border: `1px solid ${bsColor.border}` }} />
                          </Box>
                          <Typography fontSize={TYPE.caption} color={BRAND_SOFT} mb={1} fontFamily={FONT_BODY}>{r.current_role || "—"}{r.current_company ? ` · ${r.current_company}` : ""}</Typography>
                          <Box mb={1.5}>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <Typography fontSize={TYPE.overline} color={BRAND_SOFT} fontWeight={700} letterSpacing="0.06em" fontFamily={FONT_BODY}>SKILL MATCH</Typography>
                              <Typography fontSize={TYPE.overline} fontWeight={700} color={palette.color} fontFamily={FONT_BODY}>{pct}%</Typography>
                            </Box>
                            <LinearProgress variant="determinate" value={pct}
                              sx={{ height: 6, borderRadius: R_PILL, bgcolor: "#f1f5f9", "& .MuiLinearProgress-bar": { borderRadius: R_PILL, bgcolor: isPerfect ? "#15803d" : palette.color } }} />
                          </Box>
                          <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                            {r.matchedSkills.map((ms, mi) => (
                              <Chip key={mi} label={ms} size="small" sx={{ ...tagSx, bgcolor: palette.color, color: "#fff", fontWeight: 600 }} />
                            ))}
                            {allSkillsArr.filter(s => !r.matchedSkills.some(m => s.toLowerCase().includes(m.toLowerCase()))).slice(0, 3).map((s, si) => (
                              <Chip key={si} label={s} size="small" sx={{ ...tagSx, bgcolor: "#f8fafc", color: "#64748b", border: "1px solid #e2e8f0" }} />
                            ))}
                          </Box>
                          <Box display="flex" gap={2} flexWrap="wrap">
                            <Typography fontSize={TYPE.caption} color={BRAND_SOFT} fontFamily={FONT_BODY}><strong style={{ color: "#475569" }}>{r.experience} yrs</strong> exp</Typography>
                            {r.expected_salary > 0 && <Typography fontSize={TYPE.caption} color={BRAND_SOFT} fontFamily={FONT_BODY}><strong style={{ color: "#475569" }}>{fmtSalary(r.expected_salary)}</strong> expected</Typography>}
                            {r.location && <Typography fontSize={TYPE.caption} color={BRAND_SOFT} fontFamily={FONT_BODY}>📍 <strong style={{ color: "#475569" }}>{r.location}</strong></Typography>}
                          </Box>
                        </Box>
                      } />
                    </ListItem>
                  </React.Fragment>
                );
              })}
            </List>
          )
        )}

        {/* Tab 1: Bench */}
        {!loading && !error && tab === 1 && (
          <>
            <Box px={3} py={1.5} bgcolor="#FAF7FF" borderBottom={`1px solid ${BRAND_TINT2}`} display="flex" alignItems="center" flexWrap="wrap" gap={1.5}>
              <Typography fontSize={TYPE.caption} color={BRAND_SOFT} fontFamily={FONT_BODY}>Filter:</Typography>
              <Chip label={availOnly ? "✓ Available only" : "All bench"} size="small"
                onClick={() => setAvailOnly(p => !p)}
                sx={{ ...pillSx, cursor: "pointer", bgcolor: availOnly ? "#e8f5e9" : "#fff", color: availOnly ? "#2e7d32" : "#64748b", border: `1px solid ${availOnly ? "#a5d6a7" : "#e0e0e0"}` }} />
              <Typography fontSize={TYPE.caption} color={BRAND_SOFT} sx={{ ml: "auto" }} fontFamily={FONT_BODY}>{availableNow} available now · {filteredBench.length} shown</Typography>
            </Box>
            {filteredBench.length === 0 ? (
              <Box sx={emptySx}>
                <People sx={{ fontSize: 40, color: "#e0e0e0" }} />
                <Typography fontSize={TYPE.body} color={BRAND_SOFT} fontWeight={500} fontFamily={FONT_BODY}>No bench people found</Typography>
              </Box>
            ) : (
              <List disablePadding>
                {filteredBench.map((b, i) => {
                  const allSkillsArr = getSkillNames(b.skills);
                  const pct = Math.round((b.matchCount / total) * 100);
                  const isAvailable = b.status === "Available";
                  const sColor = BENCH_STATUS_COLOR[b.status] || BENCH_STATUS_COLOR["On Hold"];
                  const etColor = EMPLOYMENT_TYPE_COLOR[b.employment_type] || "#475569";
                  return (
                    <React.Fragment key={b._id}>
                      {i > 0 && <Divider />}
                      <ListItem alignItems="flex-start" sx={{
                        px: { xs: 1.75, sm: 3 },
                        py: 2,
                        bgcolor: isAvailable ? "#fafffe" : "transparent",
                        "&:hover": { bgcolor: isAvailable ? "#f0fdf4" : "#FAF7FF" },
                        borderLeft: isAvailable ? "3px solid #4caf50" : "3px solid transparent",
                      }}>
                        <ListItemAvatar sx={{ minWidth: 56 }}>
                          <Box sx={{ position: "relative", display: "inline-block" }}>
                            <Avatar sx={{ bgcolor: isAvailable ? "#2e7d32" : "#7b1fa2", fontWeight: 700, fontSize: 13, width: 40, height: 40, boxShadow: isAvailable ? "0 0 0 2px #a5d6a7" : "none", fontFamily: FONT_BODY }}>
                              {nameInitials(b.name)}
                            </Avatar>
                            <Box sx={{ position: "absolute", bottom: -4, right: -6, bgcolor: isAvailable ? "#2e7d32" : "#7b1fa2", color: "#fff", fontSize: TYPE.overline, fontWeight: 700, px: 0.5, borderRadius: "4px", border: "1.5px solid #fff", lineHeight: 1.4 }}>
                              {b.matchCount}/{total}
                            </Box>
                          </Box>
                        </ListItemAvatar>
                        <ListItemText disableTypography primary={
                          <Box>
                            <Box display="flex" alignItems="center" gap={1} mb={0.5} flexWrap="wrap">
                              <Typography fontWeight={700} fontSize={TYPE.body} color={BRAND_DEEP} fontFamily={FONT_BODY}>{b.name}</Typography>
                              {isAvailable && <Chip label="🟢 Available Now" size="small" sx={{ ...pillSx, bgcolor: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7" }} />}
                              <Chip label={b.status} size="small" sx={{ ...pillSx, bgcolor: sColor.bg, color: sColor.color, border: `1px solid ${sColor.border}` }} />
                              <Chip label={b.employment_type || "Permanent"} size="small" sx={{ ...pillSx, bgcolor: `${etColor}18`, color: etColor }} />
                            </Box>
                            <Typography fontSize={TYPE.caption} color={BRAND_SOFT} mb={1} fontFamily={FONT_BODY}>{b.current_role || "—"}{b.last_client ? ` · Last client: ${b.last_client}` : ""}</Typography>
                            <Box mb={1.5}>
                              <Box display="flex" justifyContent="space-between" mb={0.5}>
                                <Typography fontSize={TYPE.overline} color={BRAND_SOFT} fontWeight={700} letterSpacing="0.06em" fontFamily={FONT_BODY}>SKILL MATCH</Typography>
                                <Typography fontSize={TYPE.overline} fontWeight={700} color={isAvailable ? "#2e7d32" : palette.color} fontFamily={FONT_BODY}>{pct}%</Typography>
                              </Box>
                              <LinearProgress variant="determinate" value={pct}
                                sx={{ height: 6, borderRadius: R_PILL, bgcolor: "#f1f5f9", "& .MuiLinearProgress-bar": { borderRadius: R_PILL, bgcolor: isAvailable ? "#4caf50" : palette.color } }} />
                            </Box>
                            <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                              {b.matchedSkills.map((ms, mi) => (
                                <Chip key={mi} label={ms} size="small" sx={{ ...tagSx, bgcolor: isAvailable ? "#2e7d32" : palette.color, color: "#fff", fontWeight: 600 }} />
                              ))}
                              {allSkillsArr.filter(s => !b.matchedSkills.some(m => s.toLowerCase().includes(m.toLowerCase()))).slice(0, 3).map((s, si) => (
                                <Chip key={si} label={s} size="small" sx={{ ...tagSx, bgcolor: "#f8fafc", color: "#64748b", border: "1px solid #e2e8f0" }} />
                              ))}
                            </Box>
                            <Box display="flex" gap={2} flexWrap="wrap">
                              <Typography fontSize={TYPE.caption} color={BRAND_SOFT} fontFamily={FONT_BODY}><strong style={{ color: "#475569" }}>{b.experience} yrs</strong> exp</Typography>
                              {b.expected_salary > 0 && <Typography fontSize={TYPE.caption} color={BRAND_SOFT} fontFamily={FONT_BODY}><strong style={{ color: "#475569" }}>{fmtSalary(b.expected_salary)}</strong> expected</Typography>}
                              {b.location && <Typography fontSize={TYPE.caption} color={BRAND_SOFT} fontFamily={FONT_BODY}>📍 <strong style={{ color: "#475569" }}>{b.location}</strong></Typography>}
                            </Box>
                          </Box>
                        } />
                      </ListItem>
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </>
        )}

        {/* Tab 2: Insights */}
        {!loading && tab === 2 && (
          <Box p={3}>
            {insightsLoad && <Box sx={emptySx}><CircularProgress size={32} sx={{ color: BRAND_DARK }} /></Box>}
            {!insightsLoad && !insights && (
              <Box sx={emptySx}>
                <Typography fontSize={TYPE.body} color={COLORS.error} fontFamily={FONT_BODY}>Could not load insights.</Typography>
                {insightsError && (
                  <Button variant="outlined" onClick={() => setInsightsRetry(n => n + 1)}
                    sx={{ height: 36, px: 2, textTransform: "none", fontWeight: 600, borderRadius: R_SM, color: BRAND_DARK, borderColor: BRAND_SOFT }}>
                    Retry
                  </Button>
                )}
              </Box>
            )}
            {!insightsLoad && insights && (
              <Box display="flex" flexDirection="column" gap={3}>
                <Card variant="outlined" sx={{ borderRadius: R_MD, borderColor: palette.light }}>
                  <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
                    <Typography fontSize={TYPE.overline} fontWeight={700} color={BRAND_SOFT} textTransform="uppercase" letterSpacing="0.06em" mb={1.5} fontFamily={FONT_BODY}>Demand vs Supply</Typography>
                    <Grid container spacing={2}>
                      {[
                        { label: "Open Jobs",       value: insights.open_jobs,       color: "#c62828" },
                        { label: "Available Now",    value: insights.bench_available, color: "#2e7d32" },
                        { label: "Total Candidates", value: insights.candidate_total, color: palette.color },
                        { label: "Bench Total",      value: insights.bench_total,     color: "#7b1fa2" },
                      ].map(({ label, value, color }) => (
                        <Grid item xs={6} sm={3} key={label}>
                          <Box textAlign="center" p={2} sx={{ height: "100%", boxSizing: "border-box", bgcolor: `${color}0d`, borderRadius: R_SM, border: `1px solid ${color}20` }}>
                            <Typography fontSize={TYPE.stat} fontWeight={800} color={color} lineHeight={1} fontFamily={FONT_BODY} sx={{ fontVariantNumeric: "tabular-nums" }}>{value}</Typography>
                            <Typography fontSize={TYPE.overline} color={BRAND_SOFT} fontWeight={700} textTransform="uppercase" letterSpacing="0.06em" mt={1} fontFamily={FONT_BODY}>{label}</Typography>
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </CardContent>
                </Card>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 1.5, gap: 1.5, borderTop: `1px solid ${BRAND_TINT2}`, bgcolor: "#FAF7FF" }}>
        <Typography fontSize={TYPE.caption} color="text.disabled" flex={1} minWidth={0} fontFamily={FONT_BODY} sx={ELLIPSIS}>{skill.skill_name} · {skill.category}</Typography>
        <Button onClick={onClose} sx={{ height: 36, px: 2, borderRadius: R_SM, textTransform: "none", color: "#64748b", fontWeight: 600 }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function Skills() {
  // Rendered standalone at /skills (App.js) vs. as a tab inside RecruiterDashboard
  const standalone = useLocation().pathname.replace(/\/+$/, "") === "/skills";
  const [skills, setSkills]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState("");
  const [search, setSearch]       = useState("");
  const [categoryF, setCategoryF] = useState("");
  const [demandF, setDemandF]     = useState("");
  const [formOpen, setFormOpen]   = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selected, setSelected]   = useState(null);
  const [formData, setFormData]   = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [formError, setFormError] = useState("");
  const [deleting, setDeleting]   = useState(false);
  const [deleteError, setDeleteError] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [matchOpen, setMatchOpen] = useState(false);
  const [matchSkill, setMatchSkill] = useState(null);
  const [searchResults, setSearchResults]   = useState(null);
  const [searchLoading, setSearchLoading]   = useState(false);
  const [searchTab, setSearchTab]           = useState(0);
  const searchTimerRef = useRef(null);
  const loadedOnceRef  = useRef(false);

  useEffect(() => {
    const q = search.trim();
    if (!q) { setSearchResults(null); setSearchLoading(false); return; }
    setSearchLoading(true);
    const controller = new AbortController();
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(async () => {
      try {
        const [resumeRes, benchRes] = await Promise.all([
          talentSearchResumes(q, controller.signal).catch(() => ({ data: [] })),
          talentSearchBench(q, controller.signal).catch(() => ({ data: [] })),
        ]);
        // A newer query superseded this one — drop the stale response
        if (controller.signal.aborted) return;
        setSearchResults({ candidates: resumeRes.data || [], bench: benchRes.data || [], query: q });
      } catch {
        if (!controller.signal.aborted) setSearchResults({ candidates: [], bench: [], query: q });
      } finally {
        if (!controller.signal.aborted) setSearchLoading(false);
      }
    }, 400);
    return () => { clearTimeout(searchTimerRef.current); controller.abort(); };
  }, [search]);

  const openSkillMatch = (skill, e) => { e?.stopPropagation(); setMatchSkill(skill); setMatchOpen(true); };

  // First load shows the full-page spinner; later refreshes keep the table rendered
  const load = useCallback(async () => {
    const initial = !loadedOnceRef.current;
    try {
      if (initial) setLoading(true); else setRefreshing(true);
      setError("");
      const res = await getAllSkills();
      setSkills(res.data || []);
    } catch (err) { setError(errMsg(err, "Failed to load skills")); }
    finally {
      loadedOnceRef.current = true;
      setLoading(false); setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = skills.filter(s => {
    const q = search.toLowerCase();
    return (!q || s.skill_name?.toLowerCase().includes(q))
      && (!categoryF || s.category === categoryF)
      && (!demandF || s.demand_level === demandF);
  });

  const stats = {
    total:      skills.length,
    highDemand: skills.filter(s => ["High", "Critical"].includes(s.demand_level)).length,
    categories: new Set(skills.map(s => s.category)).size,
    candidates: skills.reduce((sum, s) => sum + (s.candidate_count || 0), 0),
    benchAvail: skills.reduce((sum, s) => sum + (s.bench_available || 0), 0),
  };

  const openCreate = () => { setSelected(null); setFormData(EMPTY_FORM); setFormError(""); setFormOpen(true); };
  const openEdit   = s  => { setSelected(s); setFormData({ ...EMPTY_FORM, ...s }); setFormError(""); setFormOpen(true); };
  const openDelete = s  => { setSelected(s); setDeleteError(""); setDeleteOpen(true); };
  const closeForm   = () => { if (!saving) setFormOpen(false); };
  const closeDelete = () => { if (!deleting) setDeleteOpen(false); };
  const handleChange = e => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSave = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true); setFormError("");
    try {
      if (selected) await updateSkill(selected._id, formData);
      else          await createSkill(formData);
      setFormOpen(false); load();
    } catch (err) { setFormError(errMsg(err, "Save failed")); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    if (deleting) return;
    setDeleting(true); setDeleteError("");
    try { await deleteSkill(selected._id); setDeleteOpen(false); load(); }
    catch (err) { setDeleteError(errMsg(err, "Delete failed")); }
    finally { setDeleting(false); }
  };

  if (loading)
    return (
      <ThemeProvider theme={recruiterTheme}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={36} thickness={4} sx={{ color: BRAND_DARK }} />
        </Box>
      </ThemeProvider>
    );

  return (
    <ThemeProvider theme={recruiterTheme}>
    <PageRoot $standalone={standalone}>
    <Box sx={{ width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
      {error && skills.length > 0 && <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: R_SM, mb: 2 }}>{error}</Alert>}

      {/* ── Page Header ── */}
      <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between"
        alignItems={{ xs: "stretch", sm: "center" }} flexWrap="wrap" gap={2} mb={3}>
        <Box minWidth={0}>
          <GradientHeading variant="h5" sx={{ fontSize: { xs: `${TYPE.section}px !important`, sm: `${TYPE.page}px !important` } }}>
            Skills Matrix
          </GradientHeading>
          <Typography fontSize={TYPE.body} lineHeight={1.5} color={BRAND_SOFT} mt={0.5} fontFamily={FONT_BODY} fontWeight={500}>
            Manage skill requirements and surface matched candidates
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={openCreate}
          sx={{
            textTransform: "none", fontWeight: 600, borderRadius: R_SM, fontFamily: FONT_BODY,
            bgcolor: BRAND_DARK, "&:hover": { bgcolor: BRAND_DEEP },
            boxShadow: "0 2px 8px rgba(79,55,139,0.35)",
            height: 36, px: 2,
            width: { xs: "100%", sm: "auto" },
            flexShrink: 0,
          }}
        >
          Add Skill
        </Button>
      </Box>

      {/* ── Stat Cards ── */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", sm: "repeat(auto-fit, minmax(180px, 1fr))" }, gap: 2, mb: 3 }}>
        <Box minWidth={0}>
          <StatCard label="Total Skills"    value={stats.total}      icon={<Layers />}      accent={BRAND} />
        </Box>
        {/* <Grid item xs={6} sm={6} md={4} lg={2.4}>
          <StatCard label="High Demand"     value={stats.highDemand} icon={<TrendingUp />}  accent="#dc2626" />
        </Grid>
        <Grid item xs={6} sm={6} md={4} lg={2.4}>
          <StatCard label="Categories"      value={stats.categories} icon={<Category />}    accent="#0369a1" />
        </Grid>
        <Grid item xs={6} sm={6} md={6} lg={2.4}>
          <StatCard label="Candidate Links" value={stats.candidates} icon={<People />}      accent="#15803d" />
        </Grid> */}
        <Box minWidth={0}>
          <StatCard label="Bench Available" value={stats.benchAvail} icon={<WorkOutline />} accent="#7b1fa2" />
        </Box>
      </Box>

      {/* ── Filters (always rendered so search/filters stay usable) ── */}
        <Box
          display="flex" gap={1.5} flexWrap="wrap" alignItems="center"
          sx={{ bgcolor: "#fff", border: `1px solid ${BRAND_TINT2}`, borderRadius: R_LG, boxShadow: CARD_SHADOW, p: 2, mb: 3 }}
        >
          <TextField
            placeholder="Search skills or find people by skill name…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            size="small"
            sx={{
              flexGrow: 1, minWidth: { xs: "100%", sm: 220 },
              "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: R_SM, height: 40 },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" sx={{ color: BRAND_SOFT }} />
                </InputAdornment>
              ),
              endAdornment: search ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
          <TextField
            select value={categoryF} onChange={e => setCategoryF(e.target.value)}
            size="small" label="Category"
            sx={{ minWidth: { xs: "calc(50% - 6px)", sm: 176 }, flexGrow: { xs: 1, sm: 0 }, "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: R_SM, height: 40 } }}
          >
            <MenuItem value="">All Categories</MenuItem>
            {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
          </TextField>
          <TextField
            select value={demandF} onChange={e => setDemandF(e.target.value)}
            size="small" label="Demand"
            sx={{ minWidth: { xs: "calc(50% - 6px)", sm: 136 }, flexGrow: { xs: 1, sm: 0 }, "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: R_SM, height: 40 } }}
          >
            <MenuItem value="">All Demand</MenuItem>
            {DEMAND_LEVELS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
          </TextField>
          {(search || categoryF || demandF) && (
            <Button
              onClick={() => { setSearch(""); setCategoryF(""); setDemandF(""); }}
              sx={{ height: 36, px: 2, textTransform: "none", color: "#64748b", fontWeight: 600, borderRadius: R_SM }}
            >
              Clear
            </Button>
          )}
        </Box>

      {/* ── Live Talent Search Results ── */}
      {search.trim() && (
        <Card elevation={0} sx={{ border: `1px solid ${BRAND_TINT2}`, borderRadius: R_MD, overflow: "hidden", mb: 3, boxShadow: CARD_SHADOW }}>
          <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 0, bgcolor: "#FAF7FF", borderBottom: `1px solid ${BRAND_TINT2}` }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1} mb={1}>
              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" minWidth={0}>
                <Search sx={{ fontSize: 18, color: BRAND_SOFT, flexShrink: 0 }} />
                <Typography fontWeight={700} fontSize={TYPE.body} color={BRAND_DEEP} fontFamily={FONT_BODY} sx={{ wordBreak: "break-word" }}>
                  Talent search: "<span style={{ color: BRAND_DARK }}>{search}</span>"
                </Typography>
                {searchLoading && <CircularProgress size={16} sx={{ color: BRAND_DARK }} />}
                {!searchLoading && searchResults && (
                  <Chip
                    label={`${searchResults.candidates.length + searchResults.bench.length} people found`}
                    size="small"
                    sx={{ ...pillSx, bgcolor: BRAND_TINT2, color: "#1a237e" }}
                  />
                )}
              </Box>
              <Typography fontSize={TYPE.caption} color="text.disabled" fontFamily={FONT_BODY} sx={{ display: { xs: "none", sm: "block" } }}>Searching candidates + bench people</Typography>
            </Box>

            {!searchLoading && searchResults && (
              <Tabs value={searchTab} onChange={(_, v) => setSearchTab(v)}
                sx={{ minHeight: 44, "& .MuiTab-root": { fontSize: TYPE.small, fontWeight: 600, textTransform: "none", minHeight: 44, fontFamily: FONT_BODY } }}>
                <Tab label={
                  <Box display="flex" alignItems="center" gap={1}>
                    Candidates
                    <Chip label={searchResults.candidates.length} size="small"
                      sx={{ ...countSx, bgcolor: BRAND_TINT2, color: "#1a237e" }} />
                  </Box>
                } />
                <Tab label={
                  <Box display="flex" alignItems="center" gap={1}>
                    Bench People
                    <Chip label={searchResults.bench.length} size="small"
                      sx={{
                        ...countSx,
                        bgcolor: searchResults.bench.filter(b => b.status === "Available").length > 0 ? "#e8f5e9" : "#f5f5f5",
                        color:   searchResults.bench.filter(b => b.status === "Available").length > 0 ? "#2e7d32" : "#757575",
                      }} />
                  </Box>
                } />
              </Tabs>
            )}
          </Box>

          {searchLoading && (
            <Box>
              <LinearProgress />
              <Box sx={emptySx}>
                <Typography fontSize={TYPE.body} color={BRAND_SOFT} fontFamily={FONT_BODY}>Searching across all talent pools…</Typography>
              </Box>
            </Box>
          )}

          {!searchLoading && searchResults && (
            searchTab === 0 ? (
              searchResults.candidates.length === 0 ? (
                <Box sx={emptySx}>
                  <People sx={{ fontSize: 40, color: "#e0e0e0" }} />
                  <Typography color={BRAND_SOFT} fontWeight={500} fontSize={TYPE.body} fontFamily={FONT_BODY}>No candidates found with "{search}" skill</Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {searchResults.candidates.map((r, i) => {
                    const allSkillsArr = getSkillNames(r.skills);
                    const matchedSkills = allSkillsArr.filter(s => isMatch(s, search));
                    const otherSkills   = allSkillsArr.filter(s => !isMatch(s, search)).slice(0, 4);
                    const statusStyle = {
                      New: { bg: "#f5f5f5", color: "#757575" }, "In Review": { bg: "#e3f2fd", color: "#0277bd" },
                      Shortlisted: { bg: BRAND_TINT2, color: "#1a237e" }, Interviewed: { bg: "#fff3e0", color: "#e65100" },
                      Offered: { bg: "#e8f5e9", color: "#2e7d32" }, Hired: { bg: "#e8f5e9", color: "#2e7d32" },
                      Rejected: { bg: "#fce4ec", color: "#c62828" }, "On Hold": { bg: "#fff8e1", color: "#f9a825" },
                    }[r.status] || { bg: "#f5f5f5", color: "#757575" };
                    return (
                      <React.Fragment key={r._id}>
                        {i > 0 && <Divider />}
                        <ListItem alignItems="flex-start" sx={{ px: { xs: 2, sm: 3 }, py: 2, "&:hover": { bgcolor: "#FAF7FF" } }}>
                          <ListItemAvatar sx={{ minWidth: 56 }}>
                            <Avatar sx={{ width: 40, height: 40, fontSize: 13, fontWeight: 700, bgcolor: BRAND_DARK, fontFamily: FONT_BODY }}>{nameInitials(r.name)}</Avatar>
                          </ListItemAvatar>
                          <ListItemText disableTypography primary={
                            <Box>
                              <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                                <Typography fontWeight={700} fontSize={TYPE.body} color={BRAND_DEEP} fontFamily={FONT_BODY}>{r.name}</Typography>
                                <Box sx={{ ...boxPillSx, bgcolor: statusStyle.bg, color: statusStyle.color }}>{r.status}</Box>
                              </Box>
                              <Typography fontSize={TYPE.caption} color={BRAND_SOFT} mb={1} fontFamily={FONT_BODY}>
                                {r.current_role || "—"}{r.current_company ? ` · ${r.current_company}` : ""}{r.experience ? ` · ${r.experience} yrs` : ""}{r.location ? ` · 📍 ${r.location}` : ""}
                              </Typography>
                              <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                                {matchedSkills.map((s, si) => (
                                  <Chip key={si} label={s} size="small" sx={{ ...tagSx, bgcolor: BRAND_DARK, color: "#fff", fontWeight: 700 }} />
                                ))}
                                {otherSkills.map((s, si) => (
                                  <Chip key={si} label={s} size="small" sx={{ ...tagSx, bgcolor: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0" }} />
                                ))}
                                {allSkillsArr.length > matchedSkills.length + 4 && (
                                  <Chip label={`+${allSkillsArr.length - matchedSkills.length - 4} more`} size="small" sx={{ ...tagSx, bgcolor: "#f1f5f9", color: "#94a3b8" }} />
                                )}
                              </Box>
                              <Box display="flex" gap={2} flexWrap="wrap">
                                {r.expected_salary > 0 && <Typography fontSize={TYPE.caption} color={BRAND_SOFT} fontFamily={FONT_BODY}><strong style={{ color: "#475569" }}>{fmtSalary(r.expected_salary)}</strong> expected</Typography>}
                                {r.notice_period && <Typography fontSize={TYPE.caption} color={BRAND_SOFT} fontFamily={FONT_BODY}><strong style={{ color: "#475569" }}>{r.notice_period}</strong> notice</Typography>}
                              </Box>
                            </Box>
                          } />
                        </ListItem>
                      </React.Fragment>
                    );
                  })}
                </List>
              )
            ) : (
              searchResults.bench.length === 0 ? (
                <Box sx={emptySx}>
                  <WorkOutline sx={{ fontSize: 40, color: "#e0e0e0" }} />
                  <Typography color={BRAND_SOFT} fontWeight={500} fontSize={TYPE.body} fontFamily={FONT_BODY}>No bench people found with "{search}" skill</Typography>
                </Box>
              ) : (
                <List disablePadding>
                  {[...searchResults.bench]
                    .sort((a, b) => (a.status === "Available" ? -1 : b.status === "Available" ? 1 : 0))
                    .map((b, i) => {
                      const allSkillsArr  = getSkillNames(b.skills);
                      const matchedSkills = allSkillsArr.filter(s => isMatch(s, search));
                      const otherSkills   = allSkillsArr.filter(s => !isMatch(s, search)).slice(0, 4);
                      const isAvailable   = b.status === "Available";
                      const sColor = BENCH_STATUS_COLOR[b.status] || { bg: "#f5f5f5", color: "#757575", border: "#e0e0e0" };
                      const etColor = EMPLOYMENT_TYPE_COLOR[b.employment_type] || "#475569";
                      return (
                        <React.Fragment key={b._id}>
                          {i > 0 && <Divider />}
                          <ListItem alignItems="flex-start" sx={{
                            px: { xs: 2, sm: 3 }, py: 2,
                            bgcolor: isAvailable ? "#fafffe" : "transparent",
                            "&:hover": { bgcolor: isAvailable ? "#f0fdf4" : "#FAF7FF" },
                            borderLeft: isAvailable ? "3px solid #4caf50" : "3px solid transparent",
                          }}>
                            <ListItemAvatar sx={{ minWidth: 56 }}>
                              <Avatar sx={{ width: 40, height: 40, fontSize: 13, fontWeight: 700, bgcolor: isAvailable ? "#2e7d32" : "#7b1fa2", boxShadow: isAvailable ? "0 0 0 2px #a5d6a7" : "none", fontFamily: FONT_BODY }}>
                                {nameInitials(b.name)}
                              </Avatar>
                            </ListItemAvatar>
                            <ListItemText disableTypography primary={
                              <Box>
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
                                  <Typography fontWeight={700} fontSize={TYPE.body} color={BRAND_DEEP} fontFamily={FONT_BODY}>{b.name}</Typography>
                                  {isAvailable && <Box sx={{ ...boxPillSx, bgcolor: "#e8f5e9", color: "#2e7d32", border: "1px solid #a5d6a7" }}>🟢 Available Now</Box>}
                                  <Box sx={{ ...boxPillSx, bgcolor: sColor.bg, color: sColor.color, border: `1px solid ${sColor.border}` }}>{b.status}</Box>
                                  <Box sx={{ ...boxPillSx, bgcolor: `${etColor}18`, color: etColor }}>{b.employment_type || "Permanent"}</Box>
                                </Box>
                                <Typography fontSize={TYPE.caption} color={BRAND_SOFT} mb={1} fontFamily={FONT_BODY}>
                                  {b.current_role || "—"}{b.last_client ? ` · Last: ${b.last_client}` : ""}{b.experience ? ` · ${b.experience} yrs` : ""}{b.location ? ` · 📍 ${b.location}` : ""}
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gap={0.5} mb={1}>
                                  {matchedSkills.map((s, si) => <Chip key={si} label={s} size="small" sx={{ ...tagSx, bgcolor: isAvailable ? "#2e7d32" : BRAND_DARK, color: "#fff", fontWeight: 700 }} />)}
                                  {otherSkills.map((s, si) => <Chip key={si} label={s} size="small" sx={{ ...tagSx, bgcolor: "#f1f5f9", color: "#64748b", border: "1px solid #e2e8f0" }} />)}
                                </Box>
                                <Box display="flex" gap={2} flexWrap="wrap">
                                  {b.expected_salary > 0 && <Typography fontSize={TYPE.caption} color={BRAND_SOFT} fontFamily={FONT_BODY}><strong style={{ color: "#475569" }}>{fmtSalary(b.expected_salary)}</strong> expected</Typography>}
                                  {b.notice_period && <Typography fontSize={TYPE.caption} color={BRAND_SOFT} fontFamily={FONT_BODY}><strong style={{ color: "#475569" }}>{b.notice_period}</strong> notice</Typography>}
                                </Box>
                              </Box>
                            } />
                          </ListItem>
                        </React.Fragment>
                      );
                    })}
                </List>
              )
            )
          )}
        </Card>
      )}

      {/* ── Load Error State ── */}
      {skills.length === 0 && error && (
        <Card elevation={0} sx={{ border: `1px solid ${BRAND_TINT2}`, borderRadius: R_MD, boxShadow: CARD_SHADOW }}>
          <Box sx={emptySx}>
            <Alert severity="error" sx={{ borderRadius: R_SM }}>{error}</Alert>
            <Button variant="outlined" onClick={load} disabled={refreshing}
              sx={{ height: 36, px: 2, textTransform: "none", fontWeight: 600, borderRadius: R_SM, color: BRAND_DARK, borderColor: BRAND_SOFT }}>
              {refreshing && <CircularProgress size={16} sx={{ mr: 1, color: BRAND_DARK }} />}
              Retry
            </Button>
          </Box>
        </Card>
      )}

      {/* ── Empty State ── */}
      {skills.length === 0 && !error && (
        <Card elevation={0} sx={{ border: `1px solid ${BRAND_TINT2}`, borderRadius: R_MD, boxShadow: CARD_SHADOW }}>
          <Box sx={emptySx}>
            <Avatar sx={{ width: 64, height: 64, bgcolor: BRAND_TINT2 }}>
              <Code sx={{ fontSize: 32, color: BRAND }} />
            </Avatar>
            <Typography fontWeight={700} fontSize={TYPE.cardTitle} lineHeight={1.25} color={BRAND_DEEP} fontFamily={FONT_BODY}>No skills yet</Typography>
            <Typography fontSize={TYPE.body} color={BRAND_SOFT} fontFamily={FONT_BODY}>Build your skills matrix to surface matched candidates.</Typography>
            <Button variant="contained" startIcon={<Add />} onClick={openCreate}
              sx={{ height: 36, px: 2, textTransform: "none", fontWeight: 600, borderRadius: R_SM, bgcolor: BRAND_DARK, "&:hover": { bgcolor: BRAND_DEEP } }}>
              Add First Skill
            </Button>
          </Box>
        </Card>
      )}

      {/* ── Skills Grid (kept commented out — unchanged, logic untouched) ── */}
      {/* {!search.trim() && skills.length > 0 && (
        <>
          {filtered.length === 0 ? (
            <Card elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 2 }}>
              <Box textAlign="center" py={6}>
                <Typography color="#64748b">No skills match your filters</Typography>
              </Box>
            </Card>
          ) : (
            <Box sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
                xl: "repeat(5, 1fr)",
              },
              gap: 2,
            }}>
              {filtered.map(skill => {
                const pal = CATEGORY_PALETTE[skill.category] || CATEGORY_PALETTE["Other"];
                const icon = CATEGORY_ICON[skill.category] || <Category />;
                const dm   = DEMAND_META[skill.demand_level] || DEMAND_META["Low"];
                const relatedList = (skill.related_skills || "").split(",").map(s => s.trim()).filter(Boolean);

                return (
                  <Card key={skill._id} elevation={0} sx={{
                    display: "flex", flexDirection: "column",
                    overflow: "hidden",
                    border: "1px solid #e2e8f0",
                    borderRadius: 2.5,
                    transition: "all 0.18s ease",
                    "&:hover": {
                      borderColor: pal.color,
                      boxShadow: `0 4px 20px ${pal.color}22`,
                      transform: "translateY(-2px)",
                    },
                  }}>
                   
                    <Box sx={{ height: 4, bgcolor: pal.color, flexShrink: 0 }} />

                    <CardContent sx={{ p: 2, flex: 1, display: "flex", flexDirection: "column" }}>
                   
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.5}>
                        <Box display="flex" alignItems="center" gap={1} minWidth={0} flex={1}>
                          <Box sx={{
                            width: 36, height: 36, borderRadius: 1.5, flexShrink: 0,
                            bgcolor: pal.bg, display: "flex", alignItems: "center", justifyContent: "center",
                            border: `1px solid ${pal.light}`,
                          }}>
                            {React.cloneElement(icon, { sx: { fontSize: 18, color: pal.color } })}
                          </Box>
                          <Box minWidth={0}>
                            <Tooltip title={`View candidates matching "${skill.skill_name}"`}>
                              <Typography fontWeight={700} fontSize={13} color="#0f172a"
                                onClick={e => openSkillMatch(skill, e)}
                                sx={{
                                  cursor: "pointer", lineHeight: 1.3,
                                  overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                  "&:hover": { color: pal.color, textDecoration: "underline" },
                                }}>
                                {skill.skill_name}
                              </Typography>
                            </Tooltip>
                            <Typography fontSize={10} color="#94a3b8" noWrap>{skill.category}</Typography>
                          </Box>
                        </Box>
                        <Box display="flex" gap={0.2} flexShrink={0} ml={0.5}>
                          <Tooltip title="Edit">
                            <IconButton size="small" onClick={() => openEdit(skill)} sx={{ "&:hover": { bgcolor: pal.bg } }}>
                              <Edit sx={{ fontSize: 14, color: "#94a3b8" }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" onClick={() => openDelete(skill)} sx={{ "&:hover": { bgcolor: "#fef2f2" } }}>
                              <Delete sx={{ fontSize: 14, color: "#fca5a5" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </Box>

                      
                      <Box display="flex" mb={1.5}
                        sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5, overflow: "hidden" }}>

                       
                        <Tooltip title={`View candidates with "${skill.skill_name}"`}>
                          <Box flex={1} textAlign="center" py={1}
                            onClick={e => openSkillMatch(skill, e)}
                            sx={{ cursor: "pointer", borderRight: "1px solid #e2e8f0", "&:hover": { bgcolor: pal.bg }, transition: "background 0.15s" }}>
                            <Typography fontWeight={800} fontSize={14} color={pal.color} lineHeight={1}>
                              {skill.candidate_count || 0}
                            </Typography>
                            <Typography fontSize={7.5} color="#94a3b8" fontWeight={600} textTransform="uppercase" letterSpacing="0.05em" mt={0.3}>
                              Candidates
                            </Typography>
                          </Box>
                        </Tooltip>

                 
                        <Tooltip title={`View bench people with "${skill.skill_name}"`}>
                          <Box flex={1} textAlign="center" py={1}
                            onClick={e => openSkillMatch(skill, e)}
                            sx={{ cursor: "pointer", borderRight: "1px solid #e2e8f0", "&:hover": { bgcolor: "#f3e5f5" }, transition: "background 0.15s" }}>
                            <Typography fontWeight={800} fontSize={14}
                              color={(skill.bench_count || skill.bench_total || 0) > 0 ? "#7b1fa2" : "#94a3b8"}
                              lineHeight={1}>
                              {skill.bench_count || skill.bench_total || 0}
                            </Typography>
                            <Typography fontSize={7.5} color="#94a3b8" fontWeight={600} textTransform="uppercase" letterSpacing="0.05em" mt={0.3}>
                              Bench
                            </Typography>
                          </Box>
                        </Tooltip>

                   
                        <Tooltip title={`${skill.bench_available || 0} bench people available immediately`}>
                          <Box flex={1} textAlign="center" py={1}
                            onClick={e => openSkillMatch(skill, e)}
                            sx={{ cursor: "pointer", borderRight: "1px solid #e2e8f0", "&:hover": { bgcolor: "#f0fdf4" }, transition: "background 0.15s" }}>
                            <Typography fontWeight={800} fontSize={14}
                              color={skill.bench_available > 0 ? "#2e7d32" : "#94a3b8"}
                              lineHeight={1}>
                              {skill.bench_available || 0}
                            </Typography>
                            <Typography fontSize={7.5} color="#94a3b8" fontWeight={600} textTransform="uppercase" letterSpacing="0.05em" mt={0.3}>
                              Avail Now
                            </Typography>
                          </Box>
                        </Tooltip>

                       
                        <Box flex={1} textAlign="center" py={1}>
                          <Typography fontWeight={800} fontSize={14}
                            color={(skill.job_count || 0) > 0 ? "#c2410c" : "#94a3b8"}
                            lineHeight={1}>
                            {skill.job_count || 0}
                          </Typography>
                          <Typography fontSize={7.5} color="#94a3b8" fontWeight={600} textTransform="uppercase" letterSpacing="0.05em" mt={0.3}>
                            Open Jobs
                          </Typography>
                        </Box>

                      </Box>

                  
                      <Box display="flex" alignItems="center" gap={1} mb={1} flexWrap="wrap">
                        <Box sx={{
                          px: 1, py: 0.3, borderRadius: 1, fontSize: 10, fontWeight: 700,
                          bgcolor: dm.bg, color: dm.color, border: `1px solid ${dm.border}`,
                          lineHeight: 1.4, flexShrink: 0,
                        }}>
                          {skill.demand_level} Demand
                        </Box>
                        {skill.proficiency_levels && (
                          <Typography fontSize={10} color="#94a3b8" sx={{ fontStyle: "italic" }} noWrap>
                            {skill.proficiency_levels}
                          </Typography>
                        )}
                      </Box>

                   
                      {relatedList.length > 0 && (
                        <Box mt="auto" pt={1} borderTop="1px solid #f1f5f9">
                          <Typography fontSize={9} color="#94a3b8" fontWeight={600}
                            textTransform="uppercase" letterSpacing="0.08em" mb={0.6}>
                            Related
                          </Typography>
                          <Box display="flex" flexWrap="wrap" gap={0.5}>
                            {relatedList.map((rel, ri) => (
                              <Tooltip key={ri} title="Search candidates matching all skills on this card">
                                <Chip label={rel} size="small"
                                  onClick={e => openSkillMatch(skill, e)}
                                  sx={{
                                    fontSize: 9, height: 18, borderRadius: 1,
                                    bgcolor: pal.bg, color: pal.color,
                                    border: `1px solid ${pal.light}`,
                                    fontWeight: 500, cursor: "pointer",
                                    "&:hover": { bgcolor: pal.color, color: "#fff" },
                                    transition: "all 0.15s",
                                  }} />
                              </Tooltip>
                            ))}
                          </Box>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </Box>
          )}
        </>
      )} */}

{/* ── Skills Table (also shown under talent results so name/category/demand filters stay usable) ── */}
{skills.length > 0 && (
  <>
    {search.trim() && (
      <SectionLabel>Matching skills</SectionLabel>
    )}
    {filtered.length === 0 ? (
      <Card elevation={0} sx={{ border: `1px solid ${BRAND_TINT2}`, borderRadius: R_MD, boxShadow: CARD_SHADOW }}>
        <Box sx={emptySx}>
          <Typography fontSize={TYPE.body} color={BRAND_SOFT} fontFamily={FONT_BODY}>No skills match your filters</Typography>
        </Box>
      </Card>
    ) : (
      <Card elevation={0} sx={{ border: `1px solid ${BRAND_TINT2}`, borderRadius: R_MD, overflow: "hidden", boxShadow: CARD_SHADOW }}>
        {refreshing && <LinearProgress sx={{ "& .MuiLinearProgress-bar": { bgcolor: BRAND_DARK } }} />}
        <Box sx={{ overflowX: "auto", WebkitOverflowScrolling: "touch", opacity: refreshing ? 0.6 : 1, transition: "opacity 0.15s" }}>
          <table style={{ width: "100%", minWidth: 860, borderCollapse: "collapse", tableLayout: "fixed" }}>
            <thead>
              <tr style={{ background: "#FAF7FF" }}>
                {[
                  { label: "Skill",        w: "22%", align: "left"   },
                  { label: "Category",     w: "16%", align: "left"   },
                  { label: "Demand",       w: "10%", align: "left"   },
                  { label: "Candidates",   w: "8%",  align: "center" },
                  { label: "Bench",        w: "7%",  align: "center" },
                  { label: "Avail",        w: "7%",  align: "center" },
                  { label: "Open jobs",    w: "7%",  align: "center" },
                  { label: "Related skills", w: "15%", align: "left" },
                  { label: "Actions",      w: "8%",  align: "right"  },
                ].map(({ label, w, align }) => (
                  <th key={label} style={{
                    width: w, padding: "12px 16px", textAlign: align,
                    fontSize: TYPE.overline, fontWeight: 700, color: BRAND_SOFT,
                    textTransform: "uppercase", letterSpacing: "0.06em",
                    borderBottom: `2px solid ${BRAND_TINT2}`, whiteSpace: "nowrap",
                    fontFamily: FONT_BODY,
                  }}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((skill, idx) => {
                const pal = CATEGORY_PALETTE[skill.category] || CATEGORY_PALETTE["Other"];
                const icon = CATEGORY_ICON[skill.category] || <Category />;
                const dm = DEMAND_META[skill.demand_level] || DEMAND_META["Low"];
                const relatedList = (skill.related_skills || "")
                  .split(",").map(s => s.trim()).filter(Boolean);
                const isLast = idx === filtered.length - 1;

                return (
                  <tr key={skill._id}
                    style={{ background: "#fff", transition: "background 0.12s" }}
                    onMouseEnter={e => e.currentTarget.style.background = "#FAF7FF"}
                    onMouseLeave={e => e.currentTarget.style.background = "#fff"}>

                    {/* Skill */}
                    <td style={{ padding: "12px 16px", borderBottom: isLast ? "none" : "1px solid #F5F1FB", verticalAlign: "middle" }}>
                      <Box display="flex" alignItems="center" gap={1.5} minWidth={0}>
                        <Box sx={{
                          width: 32, height: 32, borderRadius: R_SM, flexShrink: 0,
                          bgcolor: pal.bg, border: `1px solid ${pal.light}`,
                          display: "flex", alignItems: "center", justifyContent: "center",
                        }}>
                          {React.cloneElement(icon, { sx: { fontSize: 16, color: pal.color } })}
                        </Box>
                        <Box minWidth={0}>
                          <Typography fontSize={TYPE.small} fontWeight={600} color={BRAND_DEEP}
                            onClick={e => openSkillMatch(skill, e)}
                            title={skill.skill_name}
                            noWrap sx={{
                              cursor: "pointer", fontFamily: FONT_BODY,
                              "&:hover": { color: pal.color, textDecoration: "underline" },
                            }}>
                            {skill.skill_name}
                          </Typography>
                          {skill.proficiency_levels && (
                            <Typography fontSize={TYPE.overline} color={BRAND_SOFT} noWrap title={skill.proficiency_levels} sx={{ fontStyle: "italic", fontFamily: FONT_BODY }}>
                              {skill.proficiency_levels}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </td>

                    {/* Category */}
                    <td style={{ padding: "12px 16px", borderBottom: isLast ? "none" : "1px solid #F5F1FB", verticalAlign: "middle" }}>
                      <Typography fontSize={TYPE.small} color={BRAND_SOFT} noWrap title={skill.category} fontFamily={FONT_BODY}>{skill.category}</Typography>
                    </td>

                    {/* Demand */}
                    <td style={{ padding: "12px 16px", borderBottom: isLast ? "none" : "1px solid #F5F1FB", verticalAlign: "middle" }}>
                      <Box sx={{
                        display: "inline-flex", alignItems: "center",
                        height: 24, px: 1, borderRadius: R_PILL,
                        fontSize: TYPE.overline, fontWeight: 700, fontFamily: FONT_BODY,
                        bgcolor: dm.bg, color: dm.color,
                        border: `1px solid ${dm.border}`,
                        whiteSpace: "nowrap",
                      }}>
                        {skill.demand_level}
                      </Box>
                    </td>

                    {/* Candidates */}
                    <td style={{ padding: "12px 16px", textAlign: "center", borderBottom: isLast ? "none" : "1px solid #F5F1FB", verticalAlign: "middle" }}>
                      <Typography fontSize={TYPE.body} fontWeight={700} color={pal.color} fontFamily={FONT_BODY} sx={{ fontVariantNumeric: "tabular-nums", cursor: "pointer", "&:hover": { opacity: 0.7 } }}
                        onClick={e => openSkillMatch(skill, e)}>
                        {skill.candidate_count || 0}
                      </Typography>
                    </td>

                    {/* Bench */}
                    <td style={{ padding: "12px 16px", textAlign: "center", borderBottom: isLast ? "none" : "1px solid #F5F1FB", verticalAlign: "middle" }}>
                      <Typography fontSize={TYPE.body} fontWeight={700} fontFamily={FONT_BODY}
                        color={(skill.bench_count || skill.bench_total || 0) > 0 ? "#7b1fa2" : "#cbd5e1"}
                        onClick={e => openSkillMatch(skill, e)}
                        sx={{ fontVariantNumeric: "tabular-nums", cursor: "pointer", "&:hover": { opacity: 0.7 } }}>
                        {skill.bench_count || skill.bench_total || 0}
                      </Typography>
                    </td>

                    {/* Avail */}
                    <td style={{ padding: "12px 16px", textAlign: "center", borderBottom: isLast ? "none" : "1px solid #F5F1FB", verticalAlign: "middle" }}>
                      <Typography fontSize={TYPE.body} fontWeight={700} fontFamily={FONT_BODY}
                        color={skill.bench_available > 0 ? "#2e7d32" : "#cbd5e1"}
                        onClick={e => openSkillMatch(skill, e)}
                        sx={{ fontVariantNumeric: "tabular-nums", cursor: "pointer", "&:hover": { opacity: 0.7 } }}>
                        {skill.bench_available || 0}
                      </Typography>
                    </td>

                    {/* Open Jobs */}
                    <td style={{ padding: "12px 16px", textAlign: "center", borderBottom: isLast ? "none" : "1px solid #F5F1FB", verticalAlign: "middle" }}>
                      <Typography fontSize={TYPE.body} fontWeight={700} fontFamily={FONT_BODY}
                        color={(skill.job_count || 0) > 0 ? "#c2410c" : "#cbd5e1"} sx={{ fontVariantNumeric: "tabular-nums" }}>
                        {skill.job_count || 0}
                      </Typography>
                    </td>

                    {/* Related Skills */}
                    <td style={{ padding: "12px 16px", borderBottom: isLast ? "none" : "1px solid #F5F1FB", verticalAlign: "middle" }}>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {relatedList.slice(0, 2).map((rel, ri) => (
                          <Tooltip key={ri} title="Search this skill group">
                            <Chip label={rel} size="small" onClick={e => openSkillMatch(skill, e)}
                              sx={{
                                ...tagSx, maxWidth: 120,
                                bgcolor: pal.bg, color: pal.color,
                                border: `1px solid ${pal.light}`, fontWeight: 600,
                                cursor: "pointer",
                                "&:hover": { bgcolor: pal.color, color: "#fff" },
                                transition: "all 0.13s",
                              }} />
                          </Tooltip>
                        ))}
                        {relatedList.length > 2 && (
                          <Tooltip title={relatedList.slice(2).join(", ")}>
                            <Chip label={`+${relatedList.length - 2}`} size="small"
                              sx={{ ...tagSx, bgcolor: "#f1f5f9", color: "#94a3b8" }} />
                          </Tooltip>
                        )}
                      </Box>
                    </td>

                    {/* Actions */}
                    <td style={{ padding: "12px 16px", textAlign: "right", borderBottom: isLast ? "none" : "1px solid #F5F1FB", verticalAlign: "middle" }}>
                      <Box display="flex" justifyContent="flex-end" gap={0.5}>
                        <Tooltip title="Edit">
                          <IconButton size="small" onClick={() => openEdit(skill)}
                            sx={{ width: 32, height: 32, borderRadius: R_SM, "&:hover": { bgcolor: pal.bg } }}>
                            <Edit sx={{ fontSize: 16, color: "#94a3b8" }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton size="small" onClick={() => openDelete(skill)}
                            sx={{ width: 32, height: 32, borderRadius: R_SM, "&:hover": { bgcolor: "#fef2f2" } }}>
                            <Delete sx={{ fontSize: 16, color: "#fca5a5" }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Box>

        {/* Footer */}
        <Box sx={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          gap: 2, flexWrap: "wrap",
          px: 2, py: 1.5, bgcolor: "#FAF7FF", borderTop: `1px solid ${BRAND_TINT2}`,
        }}>
          <Typography fontSize={TYPE.caption} color={BRAND_SOFT} fontFamily={FONT_BODY}>
            Showing {filtered.length} of {skills.length} skills
          </Typography>
          <Typography fontSize={TYPE.caption} color={BRAND_SOFT} fontFamily={FONT_BODY}>
            {stats.candidates} candidates · {stats.benchAvail} available now
          </Typography>
        </Box>
      </Card>
    )}
  </>
)}

      {/* ── Dialogs ── */}
      <SkillResumesDialog open={matchOpen} onClose={() => setMatchOpen(false)} skill={matchSkill} />

      {/* Add / Edit Dialog */}
      <Dialog open={formOpen} onClose={closeForm} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: R_LG, m: { xs: 1, sm: 3 }, width: { xs: "calc(100% - 16px)", sm: "auto" } } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: TYPE.section, lineHeight: 1.25, color: BRAND_DEEP, borderBottom: `1px solid ${BRAND_TINT2}`, px: 3, py: 2, fontFamily: FONT_DISPLAY }}>
          {selected ? "Edit Skill" : "Add New Skill"}
        </DialogTitle>
        <form onSubmit={handleSave}>
          <DialogContent sx={{ px: 3, pt: "24px !important" }}>
            {formError && (
              <Alert severity="error" onClose={() => setFormError("")} sx={{ borderRadius: R_SM, mb: 2 }}>{formError}</Alert>
            )}
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField size="small" required fullWidth label="Skill Name"
                  name="skill_name" value={formData.skill_name} onChange={handleChange}
                  placeholder="e.g. React.js"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: R_SM } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select size="small" required fullWidth label="Category"
                  name="category" value={formData.category} onChange={handleChange}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: R_SM } }}>
                  {CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField select size="small" fullWidth label="Demand Level"
                  name="demand_level" value={formData.demand_level} onChange={handleChange}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: R_SM } }}>
                  {DEMAND_LEVELS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="Proficiency Levels"
                  name="proficiency_levels" value={formData.proficiency_levels} onChange={handleChange}
                  placeholder="e.g. Beginner, Intermediate, Advanced, Expert"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: R_SM } }} />
              </Grid>
              <Grid item xs={12}>
                <TextField size="small" fullWidth label="Related Skills (comma-separated)"
                  name="related_skills" value={formData.related_skills} onChange={handleChange}
                  placeholder="e.g. JavaScript, TypeScript, Redux"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: R_SM } }} />
              </Grid>
              <Grid item xs={12}>
                <TextField multiline rows={3} size="small" fullWidth label="Description"
                  name="description" value={formData.description} onChange={handleChange}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: R_SM } }} />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${BRAND_TINT2}`, gap: 1.5, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
            <Button onClick={closeForm} disabled={saving}
              sx={{ height: 36, px: 2, textTransform: "none", color: "#64748b", fontWeight: 600, borderRadius: R_SM }}>
              Cancel
            </Button>
            <Button type="submit" variant="contained" disabled={saving}
              sx={{ height: 36, px: 2, textTransform: "none", fontWeight: 600, borderRadius: R_SM, bgcolor: BRAND_DARK, "&:hover": { bgcolor: BRAND_DEEP } }}>
              {saving && <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />}
              {selected ? "Update" : "Create Skill"}
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteOpen} onClose={closeDelete} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: R_LG, m: { xs: 1, sm: 3 }, width: { xs: "calc(100% - 16px)", sm: "auto" } } }}>
        <DialogTitle sx={{ fontWeight: 700, fontSize: TYPE.section, lineHeight: 1.25, color: BRAND_DEEP, fontFamily: FONT_DISPLAY, px: 3, pt: 3, pb: 1 }}>Delete Skill</DialogTitle>
        <DialogContent sx={{ px: 3 }}>
          {deleteError && (
            <Alert severity="error" onClose={() => setDeleteError("")} sx={{ borderRadius: R_SM, mb: 2 }}>{deleteError}</Alert>
          )}
          <Typography fontSize={TYPE.body} lineHeight={1.5} color="#475569" fontFamily={FONT_BODY}>
            Are you sure you want to delete <strong>{selected?.skill_name}</strong>? This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5, "& > :not(style) ~ :not(style)": { ml: 0 } }}>
          <Button onClick={closeDelete} disabled={deleting} sx={{ height: 36, px: 2, textTransform: "none", color: "#64748b", fontWeight: 600, borderRadius: R_SM }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete} disabled={deleting} sx={{ height: 36, px: 2, textTransform: "none", fontWeight: 600, borderRadius: R_SM }}>
            {deleting && <CircularProgress size={16} sx={{ mr: 1, color: "#fff" }} />}
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </PageRoot>
    </ThemeProvider>
  );
}